import { useMemo } from 'react';
import { computeStandings } from '@/engine';
import { StandingsTable } from './standings-table';
import { MatchCard } from './match-card';
import { SingleElimBracket } from './single-elim-bracket';
import type { Match, Participant, Tournament } from '@/data/types';

interface Props {
  tournament: Tournament;
  matches: Match[];
  participants: Participant[];
  onSelectMatch?: (m: Match) => void;
  placeByParticipant?: Map<string, string>;
}

export function GroupsPlayoffView({
  tournament,
  matches,
  participants,
  onSelectMatch,
  placeByParticipant,
}: Props) {
  const groups = tournament.groups ?? [];
  const groupMatches = matches.filter((m) => m.stage === 'GROUP');
  const playoffMatches = matches.filter((m) => m.stage === 'PLAYOFF');

  return (
    <div className="space-y-10">
      <section>
        <div className="td-h2" style={{ marginBottom: 14 }}>Групповой этап</div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 16,
          }}
        >
          {groups.map((g) => (
            <GroupCard
              key={g.id}
              group={g}
              matches={groupMatches.filter((m) => m.groupId === g.id)}
              participants={participants}
              onSelectMatch={onSelectMatch}
              placeByParticipant={placeByParticipant}
            />
          ))}
        </div>
      </section>

      {playoffMatches.length > 0 && (
        <section>
          <div className="td-h2" style={{ marginBottom: 14 }}>Плей-офф</div>
          <SingleElimBracket
            matches={playoffMatches}
            rounds={Math.max(...playoffMatches.map((m) => m.round))}
            onSelectMatch={onSelectMatch}
            placeByParticipant={placeByParticipant}
          />
        </section>
      )}
    </div>
  );
}

function GroupCard({
  group,
  matches,
  participants,
  onSelectMatch,
  placeByParticipant,
}: {
  group: { id: string; participantIds: string[] };
  matches: Match[];
  participants: Participant[];
  onSelectMatch?: (m: Match) => void;
  placeByParticipant?: Map<string, string>;
}) {
  const groupParticipants = participants.filter((p) => group.participantIds.includes(p.id));
  const standings = useMemo(
    () => computeStandings(matches, group.participantIds),
    [matches, group.participantIds]
  );

  return (
    <div className="group-card">
      <h4>Группа {group.id}</h4>
      <StandingsTable standings={standings} participants={groupParticipants} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(248px, 1fr))',
          gap: 12,
          marginTop: 12,
        }}
      >
        {matches.map((m) => (
          <MatchCard
            key={m.id}
            match={m}
            onClick={() => onSelectMatch?.(m)}
            placeByParticipant={placeByParticipant}
          />
        ))}
      </div>
    </div>
  );
}
